import { Box, Typography } from '@mui/material';

// Donut chart with real proportions
export default function DonutChart({
                                     availableBalance = 0,
                                     totalBalance = 0,
                                     currencyBalances = {},
                                     loading = false
                                   }) {
  const bitcoinUSD = currencyBalances.bitcoin || 0;
  const ethereumUSD = currencyBalances.ethereum || 0;
  const usdUSD = currencyBalances.usd || 0;

  const balances = {
    bitcoin: bitcoinUSD,
    ethereum: ethereumUSD,
    usd: usdUSD,
  };

  const nonZeroKeys = Object.keys(balances).filter((key) => balances[key] > 0);

  let bitcoinPercent = 0;
  let ethereumPercent = 0;
  let usdPercent = 0;

  if (nonZeroKeys.length === 0) {
    bitcoinPercent = 33.33;
    ethereumPercent = 33.33;
    usdPercent = 33.34;
  } else if (nonZeroKeys.length === 1) {
    if (nonZeroKeys[0] === 'bitcoin') bitcoinPercent = 100;
    if (nonZeroKeys[0] === 'ethereum') ethereumPercent = 100;
    if (nonZeroKeys[0] === 'usd') usdPercent = 100;
  } else {
    const total = availableBalance || 1;

    bitcoinPercent = (bitcoinUSD / total) * 100;
    ethereumPercent = (ethereumUSD / total) * 100;
    usdPercent = (usdUSD / total) * 100;

    const minSegmentSize = 0.5;

    if (bitcoinPercent > 0 && bitcoinPercent < minSegmentSize) bitcoinPercent = minSegmentSize;
    if (ethereumPercent > 0 && ethereumPercent < minSegmentSize) ethereumPercent = minSegmentSize;
    if (usdPercent > 0 && usdPercent < minSegmentSize) usdPercent = minSegmentSize;
  }

  const polar = (angleDeg, r) => {
    const rad = (angleDeg - 90) * Math.PI / 180;
    return { x: r * Math.cos(rad), y: r * Math.sin(rad) };
  };

  const formatBalanceForDonut = (value) => {
    const safeValue = Number(value || 0);

    if (safeValue >= 10000) {
      return safeValue.toLocaleString('en-US', {
        maximumFractionDigits: 0,
      });
    }

    return safeValue.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const createDonutSegment = (startAngle, endAngle, radius = 100, innerRadius = 70) => {
    const sweep = endAngle - startAngle;

    if (Math.abs(sweep) >= 359.999) {
      const a0 = startAngle;
      const a1 = startAngle + 180;
      const a2 = startAngle + 360;

      const p0 = polar(a0, radius);
      const p1 = polar(a1, radius);
      const p2 = polar(a2, radius);

      const i0 = polar(a0, innerRadius);
      const i1 = polar(a1, innerRadius);
      const i2 = polar(a2, innerRadius);

      return [
        `M ${p0.x} ${p0.y}`,
        `A ${radius} ${radius} 0 1 1 ${p1.x} ${p1.y}`,
        `A ${radius} ${radius} 0 1 1 ${p2.x} ${p2.y}`,
        `L ${i2.x} ${i2.y}`,
        `A ${innerRadius} ${innerRadius} 0 1 0 ${i1.x} ${i1.y}`,
        `A ${innerRadius} ${innerRadius} 0 1 0 ${i0.x} ${i0.y}`,
        'Z',
      ].join(' ');
    }

    const pStart = polar(startAngle, radius);
    const pEnd = polar(endAngle, radius);

    const iStart = polar(startAngle, innerRadius);
    const iEnd = polar(endAngle, innerRadius);

    const largeArcFlag = Math.abs(sweep) > 180 ? 1 : 0;

    return [
      `M ${pStart.x} ${pStart.y}`,
      `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${pEnd.x} ${pEnd.y}`,
      `L ${iEnd.x} ${iEnd.y}`,
      `A ${innerRadius} ${innerRadius} 0 ${largeArcFlag} 0 ${iStart.x} ${iStart.y}`,
      'Z',
    ].join(' ');
  };

  const bitcoinAngle = (bitcoinPercent / 100) * 360;
  const ethereumAngle = (ethereumPercent / 100) * 360;
  const usdAngle = (usdPercent / 100) * 360;

  const bitcoinStart = 0;
  const bitcoinEnd = bitcoinStart + bitcoinAngle;
  const ethereumStart = bitcoinEnd;
  const ethereumEnd = ethereumStart + ethereumAngle;
  const usdStart = ethereumEnd;
  const usdEnd = usdStart + usdAngle;

  return (
    <Box sx={{ position: 'relative', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
      <svg width="240" height="240" viewBox="-120 -120 240 240">
        {bitcoinPercent > 0 && (
          <path
            d={createDonutSegment(bitcoinStart, bitcoinEnd)}
            fill="#ff9800"
            stroke="none"
          />
        )}

        {ethereumPercent > 0 && (
          <path
            d={createDonutSegment(ethereumStart, ethereumEnd)}
            fill="#884dff"
            stroke="none"
          />
        )}

        {usdPercent > 0 && (
          <path
            d={createDonutSegment(usdStart, usdEnd)}
            fill="#4caf50"
            stroke="none"
          />
        )}
      </svg>

      <Box
        sx={{
          position: 'absolute',
          textAlign: 'center',
          maxWidth: '150px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0.4,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            fontSize: '0.68rem',
            lineHeight: 1.1,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: 'rgba(255,255,255,0.65)',
          }}
        >
          Available
        </Typography>

        <Typography
          variant="h4"
          fontWeight="bold"
          sx={{
            fontSize: '1.45rem',
            lineHeight: 1.1,
            color: '#fff',
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
          }}
        >
          {loading ? 'Loading...' : `${formatBalanceForDonut(availableBalance)} $`}
        </Typography>

        <Typography
          variant="caption"
          sx={{
            fontSize: '0.72rem',
            lineHeight: 1.15,
            color: 'rgba(255,255,255,0.72)',
          }}
        >
          {loading ? '' : `Total: ${formatBalanceForDonut(totalBalance)} $`}
        </Typography>
      </Box>
    </Box>
  );
}